import axios from 'axios';

/**
 * Cliente HTTP unico.
 *
 * `baseURL` es relativa a proposito: en desarrollo la resuelve el proxy de
 * Vite y en produccion nginx, asi que el mismo build sirve cualquier dominio.
 * Nada de una URL de API incrustada en tiempo de compilacion.
 */
export const http = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 30_000,
});

let obtenerToken: () => string | null = () => null;

/** La sesion decide de donde sale el token; el cliente solo lo adjunta. */
export function registrarProveedorDeToken(proveedor: () => string | null): void {
  obtenerToken = proveedor;
}

http.interceptors.request.use((configuracion) => {
  const token = obtenerToken();
  if (token) configuracion.headers.Authorization = `Bearer ${token}`;
  return configuracion;
});

let alVencerLaSesion: () => void = () => {};

/**
 * La sesion decide que hacer cuando el servidor deja de reconocerla. El
 * cliente solo avisa.
 */
export function registrarAlVencerLaSesion(manejador: () => void): void {
  alVencerLaSesion = manejador;
}

/**
 * Sesion vencida a mitad del trabajo: un 401 en una peticion que SI llevaba
 * token. El ingreso tambien responde 401 (credenciales incorrectas) y la
 * comprobacion al recargar la maneja la propia sesion: ninguno de los dos es
 * una sesion que vencio.
 */
http.interceptors.response.use(undefined, (error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    const url = error.config?.url ?? '';
    const llevabaToken = Boolean(error.config?.headers?.Authorization);
    if (llevabaToken && !url.startsWith('/auth/')) alVencerLaSesion();
  }
  return Promise.reject(error);
});

/** Codigo HTTP de un error de la API, o `null` si no hubo respuesta. */
export function estadoHttp(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
}

/**
 * Tipo de fallo, para elegir la pantalla correcta: no es lo mismo «no existe»
 * que «no tiene permiso» que «no hay conexion».
 */
export type TipoDeFallo = 'sin-conexion' | 'no-encontrado' | 'sin-permiso' | 'conflicto' | 'otro';

export function tipoDeFallo(error: unknown): TipoDeFallo {
  if (axios.isAxiosError(error) && !error.response) return 'sin-conexion';
  switch (estadoHttp(error)) {
    case 404:
      return 'no-encontrado';
    case 403:
      return 'sin-permiso';
    case 409:
      return 'conflicto';
    default:
      return 'otro';
  }
}

export interface ErrorApi {
  codigo: string;
  mensaje: string;
  detalles?: unknown;
}

/** Extrae el error del sobre de la API, con un respaldo legible. */
export function errorDeApi(error: unknown): ErrorApi {
  if (axios.isAxiosError(error)) {
    const cuerpo = error.response?.data as { error?: ErrorApi } | undefined;
    if (cuerpo?.error) return cuerpo.error;
    if (!error.response) {
      return { codigo: 'SIN_CONEXION', mensaje: 'No se pudo contactar al servidor' };
    }
  }
  return { codigo: 'ERROR_DESCONOCIDO', mensaje: 'Ocurrió un error inesperado' };
}
